import React, { useState, useEffect } from "react";
import {
  Button,
  Row,
  Col,
  InputGroup,
  Input,
  ButtonGroup,
} from "reactstrap";
import { Triangle } from "react-loader-spinner";
import toast from "react-hot-toast";
import CustomDataTable from "../../../Components/CustomTable/CustomTable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DeleteModal from "../../../Components/Common/DeleteModal";
import MasterCostCenterService from "../../../services/OrderingServices/MasterCostCenter";
import MasterCostCenterForm from "../component/MasterCostCenterForm";
import MasterOutletService from "../../../services/OrderingServices/MasterOutletService";

const customStyles = {
  headCells: {
    style: {
      fontSize: "0.92rem",
      fontWeight: 610,
    },
  },
  rows: {
    style: {
      fontSize: "0.9rem",
    },
  },
};

const MasterCostCenter = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [selectedItem, setSelectedItem] = useState(null);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [outlet, setOutlet] = useState([]);

  const user = JSON.parse(localStorage.getItem("user"));
  const role = user?.user?.roles[0];
  const orgId = user?.organizationInfo?.id;

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await MasterCostCenterService.getList(
        role === "OrgAdmin" ? orgId : null
      );
      setData(response || []);
    } catch (error) {
      toast.error("Failed to fetch master cost centers");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs();
    fetchoutlet();
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const toggleFormModal = (item = null) => {
    setSelectedItem(item);
    setFormModalOpen((prev) => !prev);
  };

  const handleDeleteClick = (id) => {
    setSelectedItem(id);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    try {
      await MasterCostCenterService.delete(selectedItem);
      toast.success("Cost center deleted successfully.");
      fetchConfigs();
      setDeleteModal(false);
      setSelectedItem(null);
    } catch (error) {
      toast.error("Failed to delete cost center.");
    }
  };

  const fetchoutlet = async () => {
    try {
      const response = await MasterOutletService.getList(
        role === "OrgAdmin" ? orgId : null
      );
      setOutlet(response || []);
    } catch (error) {
      toast.error("Failed to fetch master outlets");
      setOutlet([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Columns ──────────────────────────────────────────────────────────────────
  const columns = [
    {
      name: "S.N",
      selector: (row, index) => index + 1,
      width: "65px",
    },
    {
      name: "Code",
      // selector: (row) => row.code || "N/A",
      cell:(row)=>(<span title={row.code}>{row.code}</span>),
      sortable: true,
    },
    {
      name: "Name",
      selector: (row) => row.name || "-",
      cell:(row)=>(<span title={row.name} style={{fontWeight:500}}>{row.name}</span>),
      sortable: true,
    },
    {
      name: "Outlet Code",
      selector: (row) => row.outletCode || "-",
      sortable: true,
    },
    {
      name: "Cost Center Type",
      selector: (row) => row.costCenterType,
      sortable: true,
    },
    {
      name: "Printer Type",
      selector: (row) => row.printerType,
      sortable: true,
    },
    {
      name: "Printer Name",
      selector: (row) => row.printerName || "-",
      sortable: true,
    },
    {
      name: "isActive",
      selector: (row) => row.isActive,
      cell: (row) => (
        <span className={`badge ${row.isActive ? "bg-success" : "bg-danger"}`}>
          {row.isActive ? "Active" : "Inactive"}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Actions",
      selector: (row) => (
        <div>
          <Button
            color="info"
            size="sm"
            className="mx-1"
            title="Edit"
            onClick={() => toggleFormModal(row)}
          >
            <i className="ri-edit-line" />
          </Button>
          <Button
            color="danger"
            size="sm"
            className="mx-1"
            title="Delete"
            onClick={() => handleDeleteClick(row.id)}
          >
            <i className="ri-delete-bin-5-line" />
          </Button>
        </div>
      ),
    },
  ];

  // ── Filter ───────────────────────────────────────────────────────────────────
  const filteredData = data.filter((row) =>
    `${row.code} ${row.name} ${row.outletCode} ${row.costCenterType} ${row.printerType} ${row.printerName}`
      .toLowerCase()
      .includes(filterText.toLowerCase())
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="page-content">
      <div className="container-fluid">
        <BreadCrumb title="Master Cost Center" pageTitle="Masters" />

        <Row className="mb-2">
          <Col md={8} xs={12}>
            <InputGroup>
              <Input
                placeholder="Search by code, name, outlet, type..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={4} xs={12} className="d-flex justify-content-end align-items-end">
            <Button color="success" onClick={() => toggleFormModal()} className="d-flex align-items-center gap-2">
              <i className="ri-add-line"></i>Create
            </Button>
          </Col>
        </Row>

        <CustomDataTable
          responsive
          striped
          pagination
          fixedHeader
          persistTableHead
          progressPending={loading}
          customStyles={customStyles}
          progressComponent={
            <div className="my-3">
              <Triangle visible height="80" width="80" color="#5B71B9" />
              <h5 className="mt-1">Loading...</h5>
            </div>
          }
          columns={columns}
          data={filteredData}
        />
      </div>

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        title="Delete Cost Center"
        description="Are you sure you want to delete this cost center?"
      />

      {/* Add / Edit Form */}
      <MasterCostCenterForm
        modal={formModalOpen}
        toggleModal={() => toggleFormModal()}
        selectedItem={selectedItem}
        fetchMasterCostCenter={fetchConfigs}
        outlet={outlet}
      />
    </div>
  );
};

export default MasterCostCenter;
